import React from 'react';
import { connect } from 'react-redux';
import { PoiId, PoiType, MarketplaceType } from 'utils/entities';
import { DecodedRouteParams, Route, useRoute } from 'config/routes';
import { ShowAt, useScreenBreakpoint } from 'consts/breakpoints';
import { searchListPoisDictionarySelector } from 'store/state/domainData/selectors';
import UniversalCard from 'components/cards/new-layout/UniversalCard';
import {
  makeMapStateToPropsFactory,
  PoiGetter,
} from 'components/cards/listing/makeMapStateToPropsFactory';
import { routeByPoiTypeAndMarketplace } from 'utils/marketplaceRoutes';
import { Link } from 'components/link';
import { ListingCardsItemModeB } from 'screens/List/styled';
import { useSearchCardEventIntersection } from 'hooks/useSearchCardEventIntersection';
import { useSearchPropertyCardEvents } from 'screens/List/useSearchPropertyCardEvents';
import { SearchViewMode } from 'components/list-view-switcher/constants';


const poiByIdSelector: PoiGetter = (state, id) => {
  const poi = searchListPoisDictionarySelector(state)[id];
  if (poi.type === 'ad') return null;
  return poi;
};

const makeMapStateToProps = makeMapStateToPropsFactory(poiByIdSelector);

const ConnectedUniversalCard = connect(makeMapStateToProps)(UniversalCard);

interface ListItemProps extends DecodedRouteParams {
  id: PoiId;
  allowToShowTagline?: boolean;
  type: PoiType;
  setActive: (ids: PoiId[]) => void;
  openYieldTagModal?: (id: string) => void;
  listIndex?: number;
  listItemPage?: number;
  isNearby?: boolean;
  isSchoolSearchDocId?: string | null;
  marketplace?: MarketplaceType;
  view: SearchViewMode;
  useListOnly?: boolean;
  addRecentlyViewed?: (id: PoiId, type: PoiType) => void;
}

const PRELOADED_IMAGES_NUM = 3;

export const ListItem: React.FC<ListItemProps> = React.memo((props) => {
  const { id, type, setActive, listIndex, isSchoolSearchDocId, listItemPage: page, allowToShowTagline, isNearby, openYieldTagModal, marketplace, view, useListOnly, addRecentlyViewed, ...shouldBeRouteParams  } = props;
  // if this type assertion breaks, destructure props from line above
  const routeParams: DecodedRouteParams = shouldBeRouteParams;
  const ref = React.useRef<HTMLDivElement>(null);
  const isMobile = useScreenBreakpoint() < 3;
  const isLargeDesktop = useScreenBreakpoint() >= 4;
  const { route } = useRoute();
  const isInvestors = route.name === Route.InvestorsSearchPage;
  const isRecentlyViewedV2 = marketplace === MarketplaceType.Residential;

  const linkRouteParams: DecodedRouteParams = {
    ...routeParams,
    id,
    page,
    tracking_event_source: 'list_regular_card',
    tracking_list_index: listIndex,
    investorMode: (route.name === Route.InvestorsSearchPage) || undefined,
  };

  const linkRouteName = routeByPoiTypeAndMarketplace(type, marketplace);

  useSearchCardEventIntersection(ref, id);
  const { onCardClick } = useSearchPropertyCardEvents(ref, id, { listIndex, page });

  return (
    <ShowAt isTouch>
      {(isTouch) => (
        <ListingCardsItemModeB
          data-auto={`listed-bulletin${isNearby ? '-nearby' : ''}`}
          data-auto-bulletin-id={id}
          data-auto-bulletin-type={type}
          key={id}
          ref={ref}
          onMouseLeave={() => isTouch ? undefined : setActive([])}
          onClick={() => isTouch ? undefined : setActive([])}
          onMouseEnter={() => isTouch ? undefined : setActive([ id ])}
          view={view}
          useListOnly={useListOnly}
        >
          <Link
            data-auto="listed-bulletin-clickable"
            routeName={linkRouteName}
            routeParams={linkRouteParams}
            hideLinkParams
            target={isMobile || isInvestors ? '_self' : '_blank'}
            onClick={() => {
              onCardClick();
              if (!isTouch && isRecentlyViewedV2 && addRecentlyViewed) {
                addRecentlyViewed(id, type);
              }
            }}
          >
            <ConnectedUniversalCard
              emptyLazyLoadPlaceholder={listIndex > (PRELOADED_IMAGES_NUM - 1)}
              id={id}
              view={view}
              allowToShowTagline={allowToShowTagline}
              small={isMobile || (useListOnly && !isLargeDesktop)}
              showSchoolInAreaLabel={isNearby ? undefined : isSchoolSearchDocId}
              openYieldTagModal={openYieldTagModal}
              isInvestors={isInvestors}
            />
          </Link>
        </ListingCardsItemModeB>
      )}
    </ShowAt>
  );
});
